import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Sentry } from "@/lib/sentry";
import { Button } from "@/components/ui/button";

// =============================================================================
// AppErrorBoundary
//
// Catches render-time errors anywhere below it. Two distinct cases:
//
//  1. **Stale lazy chunk** — the classic "blank page after a deploy" bug.
//     When we deploy, Cloud Run replaces the hashed chunk files
//     (AdminBookingsPage-abc123.js → AdminBookingsPage-def456.js). A user
//     with an old tab still references the old hash; the next client-side
//     navigation to a lazy route does a dynamic `import()` that 404s and
//     the promise rejects. Suspense has no recovery path for rejected
//     imports, so the whole tree unmounts and the screen goes blank. We
//     trigger one hard reload to pick up the new index.html + chunk map,
//     at most once every five minutes per tab (the time is kept in a
//     sessionStorage sentinel), so a chunk that keeps failing after the
//     reload gets the Refresh panel instead of reloading forever.
//
//  2. **Real render bug** — any other thrown error. We show a "something
//     went wrong" panel with a manual Refresh button rather than
//     auto-reloading (a reload loop on a deterministic crash would
//     thrash the user worse than the blank screen).
//
// The sentinel lives in sessionStorage rather than localStorage so it
// resets per tab — if a user has two tabs open and one hits a stale
// chunk error, the other keeps working.
//
// When the browser blocks storage for the site (cookies blocked, some
// embedded frames) there is no sentinel: touching sessionStorage throws,
// so every access is wrapped, and a chunk error goes straight to the
// manual panel — without the sentinel a reload could loop forever.
// =============================================================================

const RELOAD_SENTINEL_KEY = "mtform-chunk-reload-in-progress";
const RELOAD_COOLDOWN_MS = 5 * 60 * 1000;

/** Set while a reload is scheduled. React retries a failed render once,
 *  straight away, and the retry would otherwise find the sentinel just
 *  written, decide the reload had already been tried, and show the error
 *  panel until the browser got round to reloading. */
let reloadScheduled = false;

interface State {
  hasError: boolean;
  reloading: boolean;
}

interface Props {
  children: ReactNode;
}

/** Tries to recognise the shape of a "lazy import couldn't fetch the
 *  bundle" error. Different browsers throw different messages for this
 *  scenario, so we match a set of known phrasings. */
function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const message =
    error instanceof Error ? `${error.name}: ${error.message}` : String(error);
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /ChunkLoadError/i.test(message) ||
    /Loading chunk \d+ failed/i.test(message)
  );
}

/** The sentinel's value, or `undefined` when storage is blocked (reading
 *  `sessionStorage` then throws a SecurityError, even through `typeof`). */
function readSentinel(): string | null | undefined {
  try {
    return sessionStorage.getItem(RELOAD_SENTINEL_KEY);
  } catch {
    return undefined;
  }
}

/** Sets the sentinel; false when storage refuses it. */
function writeSentinel(): boolean {
  try {
    sessionStorage.setItem(RELOAD_SENTINEL_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

function clearSentinel(): void {
  try {
    sessionStorage.removeItem(RELOAD_SENTINEL_KEY);
  } catch {
    // Storage blocked: nothing can have been stored.
  }
}

export class AppErrorBoundary extends Component<Props, State> {
  override state: State = { hasError: false, reloading: false };

  static getDerivedStateFromError(error: unknown): State {
    if (isChunkLoadError(error) && typeof window !== "undefined") {
      if (reloadScheduled) return { hasError: true, reloading: true };
      const sentinel = readSentinel();
      // The sentinel holds when we last reloaded for this, and is never
      // cleared on load: the app mounts before a lazy page's chunk is
      // fetched, so clearing it on mount let a chunk that keeps failing
      // reload forever. Record this reload (writeSentinel) before making
      // it; with storage blocked (undefined) or refusing the write, don't.
      // A time in the future (the clock was set back since) isn't recent:
      // this reload rewrites it with the corrected time.
      const age = Date.now() - Number(sentinel);
      const recent = sentinel != null && age >= 0 && age < RELOAD_COOLDOWN_MS;
      if (sentinel !== undefined && !recent && writeSentinel()) {
        reloadScheduled = true;
        // Defer until after the boundary commits the error state — a
        // sync reload from inside getDerivedStateFromError tends to
        // race with React's own commit phase.
        setTimeout(() => {
          reloadScheduled = false;
          window.location.reload();
        }, 0);
        return { hasError: true, reloading: true };
      }
      // We already tried reloading and we're still hitting a chunk
      // load error (or can't safely try). Fall through to the
      // manual-recovery UI so the user isn't stuck in a hidden reload loop.
    }
    return { hasError: true, reloading: false };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error("[AppErrorBoundary] caught", error, info);

    // A stale-chunk error is an expected, self-healing deploy artifact
    // (we hard-reload to recover) — not a render bug, so don't add noise
    // to Sentry for it. Every other thrown error is a real render crash
    // worth reporting (no-op when VITE_SENTRY_DSN is unset). The React
    // component stack is non-PHI and helps pinpoint the failing subtree.
    if (!isChunkLoadError(error)) {
      Sentry.captureException(error, {
        contexts: { react: { componentStack: info.componentStack } },
      });
    }
  }

  override render() {
    if (this.state.reloading) {
      // Mid-reload: render nothing rather than the error UI. The
      // browser is about to replace this whole document anyway.
      return null;
    }
    if (this.state.hasError) {
      return (
        <main className="flex min-h-screen items-center justify-center px-4 py-10">
          <div className="w-full max-w-md rounded-2xl border border-border/70 bg-card/70 px-6 py-8 text-center shadow-[0_24px_60px_-40px_hsl(222_90%_2%/0.9)] sm:px-8">
            <span
              aria-hidden="true"
              className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-blue-700 ring-1 ring-inset ring-primary/25 dark:text-primary"
            >
              <AlertTriangle className="h-6 w-6" />
            </span>
            <h1 className="mt-4 text-balance text-lg font-bold tracking-tight text-foreground">
              Something went wrong loading this page.
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Please refresh and try again. If it keeps happening, sign out
              and back in or contact support.
            </p>
            <Button
              type="button"
              className="mt-6"
              onClick={() => {
                clearSentinel();
                window.location.reload();
              }}
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
          </div>
        </main>
      );
    }
    return this.props.children;
  }
}
